/**
 * Native Features Initialization
 * Central module for initializing all Capacitor native features
 */

import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { SplashScreen } from '@capacitor/splash-screen'
import { Keyboard } from '@capacitor/keyboard'
import { App } from '@capacitor/app'

import { initPushNotifications, setNavigateFunction as setPushNavigate, removeFCMToken, clearBadgeCount, updateBadgeCount } from './push'
import { initDeepLinks, setDeepLinkNavigate } from './deep-links'
import { initPomodoroNotificationListeners, setPomodoroNotificationNavigate } from './pomodoro-notifications'
import { initPurchases } from './purchases'

export * from './haptics'
export * from './push'
export * from './deep-links'
export * from './sounds'
export * from './purchases'

const isNative = Capacitor.isNativePlatform()
const isIOS = Capacitor.getPlatform() === 'ios'
const isAndroid = Capacitor.getPlatform() === 'android'

export { isNative, isIOS, isAndroid }

type NavigateFunction = (path: string) => void

/**
 * Initialize all native features
 * Call this once when the app starts, after the router is ready
 */
export async function initNativeFeatures(navigate: NavigateFunction): Promise<void> {
  if (!isNative) {
    console.log('Not on native platform, skipping native initialization')
    return
  }

  console.log('Initializing native features for:', Capacitor.getPlatform())

  // Set up navigation for push notifications, deep links, and pomodoro notifications
  setPushNavigate(navigate)
  setDeepLinkNavigate(navigate)
  setPomodoroNotificationNavigate(navigate)

  // Initialize status bar
  await initStatusBar()

  // Initialize keyboard handling
  await initKeyboard()

  // Initialize push notifications
  await initPushNotifications()

  // Initialize deep links
  await initDeepLinks()

  // Initialize pomodoro notification listeners (don't block on Android plugin quirks)
  await initPomodoroNotificationListeners().catch(err =>
    console.warn('Pomodoro notification listeners init error:', err)
  )

  // Initialize RevenueCat purchases
  await initPurchases().catch(err => console.warn('RevenueCat init error:', err))

  // Initialize app lifecycle listeners
  initAppLifecycle()

  // Hide splash screen after initialization
  await hideSplashScreen()

  console.log('Native features initialized successfully')
}

/**
 * Notify native iOS about theme changes.
 * This updates the native WKWebView/scrollView background colors
 * so overscroll/bounce areas show the correct color for the current theme.
 * Also updates the theme-color meta tag for PWA/browser contexts.
 */
export function notifyNativeThemeChange(isDark: boolean): void {
  try {
    // Send to iOS WKWebView message handler (registered in CustomBridgeViewController)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).webkit?.messageHandlers?.themeChange?.postMessage({ isDark })
  } catch {
    // Not on iOS or message handler not available
  }

  // On Android, update the navigation bar color to match the theme so the
  // gesture-navigation area / soft-nav buttons blend with the app background.
  if (isAndroid) {
    StatusBar.setBackgroundColor({ color: '#00000000' }).catch(() => {})
  }

  // Also update the theme-color meta tag
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', isDark ? '#020617' : '#f8fafc')
  }
}

/**
 * Initialize status bar styling
 */
async function initStatusBar(): Promise<void> {
  if (!isNative) return

  try {
    // Check if dark mode is active.
    // Only check the 'dark' class on <html> — this is the single source of truth.
    // initializeTheme() in main.tsx already maps system preference into this class,
    // so we must NOT fall back to matchMedia here (it would override user's explicit choice).
    const isDarkMode = document.documentElement.classList.contains('dark')

    // Set status bar style based on theme
    // Style.Dark = light/white text (for dark backgrounds)
    // Style.Light = dark/black text (for light backgrounds)
    await StatusBar.setStyle({ 
      style: isDarkMode ? Style.Dark : Style.Light 
    })

    // Notify native about initial theme for background colors (overscroll fix)
    notifyNativeThemeChange(isDarkMode)

    if (isAndroid) {
      // Set transparent background on Android so our CSS background shows
      await StatusBar.setBackgroundColor({ color: '#00000000' })
    }

    // Show status bar (in case it was hidden)
    await StatusBar.show()

    // Listen for system theme changes and update status bar.
    // When the system theme changes, initializeTheme()'s listener fires first
    // and updates the 'dark' class. We use a short delay to ensure the class
    // is updated before we read it. The MutationObserver below also catches this,
    // so this listener serves as an additional safety net.
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', async () => {
      // Small delay to let initializeTheme()'s listener update the dark class first
      await new Promise(resolve => setTimeout(resolve, 50))
      const newIsDark = document.documentElement.classList.contains('dark')
      try {
        await StatusBar.setStyle({ 
          style: newIsDark ? Style.Dark : Style.Light 
        })
      } catch (err) {
        console.warn('Failed to update status bar on system theme change:', err)
      }
      notifyNativeThemeChange(newIsDark)
    })

    // Also observe class changes on documentElement for manual dark mode toggle
    const observer = new MutationObserver(async (mutations) => {
      // Only process if class attribute actually changed
      const hasClassChange = mutations.some(m => m.attributeName === 'class')
      if (!hasClassChange) return

      // Check current state (not per-mutation, since mutations are batched)
      const isDark = document.documentElement.classList.contains('dark')
      try {
        await StatusBar.setStyle({ 
          style: isDark ? Style.Dark : Style.Light 
        })
      } catch (err) {
        console.warn('Failed to update status bar on class change:', err)
      }
      notifyNativeThemeChange(isDark)
    })
    observer.observe(document.documentElement, { attributes: true })
  } catch (error) {
    console.warn('Error initializing status bar:', error)
  }
}

/**
 * Initialize keyboard handling
 */
async function initKeyboard(): Promise<void> {
  if (!isNative) return

  try {
    Keyboard.addListener('keyboardWillShow', (info) => {
      console.log('Keyboard will show, height:', info.keyboardHeight)
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`)
      document.documentElement.classList.add('keyboard-open')

      if (isAndroid) {
        // With adjustPan, Android pans to the focused element natively.
        // Ensure the active element is scrolled into view as a fallback.
        setTimeout(() => {
          const el = document.activeElement as HTMLElement | null
          el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
        }, 100)
      }
    })

    Keyboard.addListener('keyboardWillHide', () => {
      console.log('Keyboard will hide')
      document.body.style.setProperty('--keyboard-height', '0px')
      document.documentElement.classList.remove('keyboard-open')
    })

    if (isIOS) {
      await Keyboard.setAccessoryBarVisible({ isVisible: true })
    }
  } catch (error) {
    console.warn('Error initializing keyboard:', error)
  }
}

/**
 * Reset keyboard-related state on Android when returning to the app.
 * Clears the CSS variable, removes the keyboard-open class, blurs
 * the active element (in case the keyboard was open in another app),
 * and forces a visual viewport resize recalculation.
 */
function resetAndroidKeyboardState(): void {
  document.body.style.setProperty('--keyboard-height', '0px')
  document.documentElement.classList.remove('keyboard-open')

  // Blur any focused input so the keyboard doesn't reappear and the
  // WebView doesn't stay in a "keyboard visible" layout state.
  const active = document.activeElement as HTMLElement | null
  if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) {
    active.blur()
  }

  // Force the WebView to recalculate its layout by toggling a tiny
  // height change. This catches edge cases where the native FrameLayout
  // height got stuck (the native onResume relayout handles the other side).
  requestAnimationFrame(() => {
    const root = document.getElementById('root')
    if (root) {
      root.style.minHeight = '99.99%'
      requestAnimationFrame(() => {
        root.style.minHeight = ''
      })
    }
  })
}

/**
 * Initialize app lifecycle listeners
 */
function initAppLifecycle(): void {
  if (!isNative) return

  // App state change (foreground/background)
  App.addListener('appStateChange', ({ isActive }) => {
    console.log('App state changed, is active:', isActive)
    
    if (isActive) {
      updateBadgeCount()

      if (isAndroid) {
        resetAndroidKeyboardState()
      }

      window.dispatchEvent(new Event('capacitor-resume'))
    }
  })

  // App resumed from pause (Android)
  App.addListener('resume', () => {
    console.log('App resumed')
    if (isAndroid) {
      resetAndroidKeyboardState()
    }
  })

  // App paused (Android)
  App.addListener('pause', () => {
    console.log('App paused')
  })

  // Back button pressed (Android)
  App.addListener('backButton', ({ canGoBack }) => {
    if (!canGoBack) {
      // At root of app - minimize to background instead of closing
      App.minimizeApp()
    } else {
      // Let the router handle back navigation
      window.history.back()
    }
  })
}

/**
 * Hide the splash screen
 */
export async function hideSplashScreen(): Promise<void> {
  if (!isNative) return

  try {
    await SplashScreen.hide({
      fadeOutDuration: 300,
    })
  } catch (error) {
    console.warn('Error hiding splash screen:', error)
  }
}

/**
 * Show the splash screen (useful for loading states)
 */
export async function showSplashScreen(): Promise<void> {
  if (!isNative) return

  try {
    await SplashScreen.show({
      autoHide: false,
    })
  } catch (error) {
    console.warn('Error showing splash screen:', error)
  }
}

/**
 * Set status bar style based on background color
 * @param style 'light' = light background (needs dark icons), 'dark' = dark background (needs light icons)
 */
export async function setStatusBarStyle(style: 'light' | 'dark'): Promise<void> {
  if (!isNative) return

  try {
    await StatusBar.setStyle({
      // Style.Dark = light/white text (for dark backgrounds)
      // Style.Light = dark/black text (for light backgrounds)
      style: style === 'light' ? Style.Light : Style.Dark,
    })
  } catch (error) {
    console.warn('Error setting status bar style:', error)
  }
}

/**
 * Hide status bar
 */
export async function hideStatusBar(): Promise<void> {
  if (!isNative) return

  try {
    await StatusBar.hide()
  } catch (error) {
    console.warn('Error hiding status bar:', error)
  }
}

/**
 * Show status bar
 */
export async function showStatusBar(): Promise<void> {
  if (!isNative) return

  try {
    await StatusBar.show()
  } catch (error) {
    console.warn('Error showing status bar:', error)
  }
}

/**
 * Clean up native features (call on logout)
 */
export async function cleanupNativeFeatures(): Promise<void> {
  if (!isNative) return

  // Remove FCM token on logout
  await removeFCMToken()
}

/**
 * Get app info
 */
export async function getAppInfo(): Promise<{ id: string; name: string; version: string; build: string } | null> {
  if (!isNative) return null

  try {
    const info = await App.getInfo()
    return {
      id: info.id,
      name: info.name,
      version: info.version,
      build: info.build,
    }
  } catch (error) {
    console.warn('Error getting app info:', error)
    return null
  }
}

/**
 * Check if running on native platform
 */
export function isNativePlatform(): boolean {
  return isNative
}

/**
 * Get current platform
 */
export function getPlatform(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web'
}
